const DEFAULT_RETRY_VALUE = 1;

/**
 * Generic async retry helper. Retries whenever `callback` throws/rejects, waiting
 * `delay` ms (if given) between attempts. An optional `shouldRetry(error)` predicate
 * can be used to filter which errors are retryable - if it returns `false`, the error
 * is re-thrown immediately without consuming further retry attempts.
 *
 * If all attempts are exhausted, the last thrown error is re-thrown.
 */
async function retry<T>(
  callback: () => Promise<T> | T,
  retryCount?: number,
  delay?: number,
  shouldRetry?: (error: unknown) => boolean,
): Promise<T> {
  retryCount = typeof retryCount === 'number' ? retryCount : DEFAULT_RETRY_VALUE;
  try {
    return await callback();
  } catch (err) {
    if (retryCount < 1 || (shouldRetry && !shouldRetry(err))) {
      throw err;
    }
    if (delay && delay > 0) {
      await new Promise<void>(resolve => setTimeout(resolve, delay));
    }
    return retry(callback, retryCount - 1, delay, shouldRetry);
  }
}

export default { retry };
